/**
 * Maximum file size in bytes (5MB)
 */
export const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Validates image format for web upload
 * Now supports all image formats including HEIC/HEIF since we re-encode to JPEG
 */
export const validateImageFormat = (file: File): boolean => {
  // Accept any file with an image MIME type
  // The browser's canvas API will handle decoding and we'll re-encode to JPEG
  return file.type.startsWith('image/');
};

/**
 * Validates file size is within the allowed limit
 */
export const validateFileSize = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

/**
 * Converts File to Uint8Array for API upload
 */
export const fileToUint8Array = async (file: File): Promise<Uint8Array> => {
  try {
    const arrayBuffer = await file.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (error) {
    console.error('File conversion failed:', error);
    throw new Error('Failed to convert image to binary format');
  }
};

/**
 * Converts image URI (data URL or object URL) to Uint8Array
 */
export const imageToUint8Array = async (uri: string): Promise<Uint8Array> => {
  try {
    const response = await fetch(uri);
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
  } catch (error) {
    console.error('Image conversion failed:', error);
    throw new Error('Failed to convert image to binary format');
  }
};

/**
 * Creates an object URL from a File for preview
 */
export const createImagePreview = (file: File): string => {
  return URL.createObjectURL(file);
};

/**
 * Revokes object URL to free memory
 */
export const revokeImagePreview = (url: string): void => {
  URL.revokeObjectURL(url);
};

/**
 * Re-encodes an image to ensure it's in a valid, standardized format
 * This fixes issues with HEIC/HEIF images and browser-specific format handling
 * @param file Original image file
 * @param quality JPEG quality (0-1), defaults to 0.9
 * @returns Re-encoded image as Blob
 */
export const reencodeImage = async (file: File, quality: number = 0.9): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      try {
        // Create canvas with image dimensions
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        // Draw image to canvas
        ctx.drawImage(img, 0, 0);

        // Convert to blob
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(objectUrl);

            if (!blob) {
              reject(new Error('Failed to re-encode image'));
              return;
            }

            resolve(blob);
          },
          'image/jpeg',
          quality
        );
      } catch (error) {
        URL.revokeObjectURL(objectUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image for re-encoding'));
    };

    img.src = objectUrl;
  });
};
